/* REACT */
import { useState } from 'react';

/* ROUTER */
import { Link } from 'react-router-dom';

/* PLUGINS */
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';

/* SERVICES */
import { authService } from '../services/authService';

/* SCHEMAS */
import { forgotPasswordSchema, type ForgotPasswordFormData } from '../schemas';

/* COMPONENTS */
import { AuthLayout } from './AuthLayout';

const AUTH_CARD_CLASS = [
    'w-full max-w-[480px] relative rounded-lg border border-[#e5e5e5] bg-white',
    'flex flex-col items-center px-8 py-8 md:px-10 gap-8 text-center',
].join(' ');
const INPUT_CLASS = [
    'flex-1 text-sm leading-5 tracking-[0.25px] border-none outline-none',
    'bg-transparent text-[#281e1b] placeholder:text-[rgba(46,27,20,0.4)]',
].join(' ');
const SUBMIT_BUTTON_CLASS = [
    'self-stretch rounded-lg bg-brand flex items-center justify-center px-4 py-3',
    'cursor-pointer text-white border-none text-sm leading-5 tracking-[0.25px]',
    'font-medium hover:bg-brand-hover disabled:bg-[#e5e5e5]',
    'disabled:text-[rgba(46,27,20,0.4)]',
].join(' ');
const HEADING_CLASS = 'self-stretch text-[28px] leading-9 font-medium text-[#363636]';
const BODY_COPY_CLASS = 'self-stretch text-base leading-6 tracking-[0.5px] text-[#525252]';
const BACK_LINK_CLASS = [
    'text-sm leading-5 tracking-[0.25px] text-[#737373]',
    'hover:text-[#171717] hover:underline',
].join(' ');
const LABEL_CLASS = [
    'self-stretch text-sm leading-5 tracking-[0.25px]',
    'font-medium text-[#281e1b]',
].join(' ');
const FIELD_ERROR_CLASS = 'text-xs leading-4 tracking-[0.25px] text-[#dc2626] mt-1';

/**
 * DOCU: Renders PO-style forgot password form. <br>
 * Triggered: User navigates to /forgot-password. <br>
 * Last Updated Date: May 19, 2026 <br>
 * @returns {JSX.Element} Forgot password screen.
 * @author Sam
 */
export const ForgotPasswordForm = () => {
    const [is_submitted, setIsSubmitted] = useState(false);
    const [is_loading, setIsLoading] = useState(false);
    const [submitted_email, setSubmittedEmail] = useState('');
    const {
        formState: { errors },
        handleSubmit,
        register,
        setError,
    } = useForm<ForgotPasswordFormData>({
        resolver: zodResolver(forgotPasswordSchema),
    });
    const email_field_class = [
        'self-stretch rounded-lg border bg-white flex items-center px-3 py-2',
        errors.email_address ? 'border-[#dc2626] border-[1.5px]' : 'border-[#e5e5e5]',
    ].join(' ');

    /**
     * DOCU: Request password reset email from backend. <br>
     * Triggered: Forgot password form submit. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {ForgotPasswordFormData} data - Email payload.
     * @returns {Promise<void>}
     * @author Sam
     */
    const onSubmit = async (data: ForgotPasswordFormData): Promise<void> => {
        setIsLoading(true);

        try {
            const response = await authService.forgotPassword(data);

            if (response.result?.is_sent) {
                setSubmittedEmail(data.email_address);
                setIsSubmitted(true);
                return;
            }

            setError('email_address', {
                type: 'manual',
                message: 'Failed to send reset email. Please try again.',
            });
        }
        catch {
            setError('email_address', {
                type: 'manual',
                message: 'An error occurred. Please try again later.',
            });
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout title="">
            <div className={AUTH_CARD_CLASS}>
                {is_submitted ? (
                    <div className="self-stretch flex flex-col items-center gap-8">
                        <div
                            className={[
                                'size-16 rounded-full bg-[#dcfce7]',
                                'flex items-center justify-center',
                            ].join(' ')}
                        >
                            <Mail className="size-8 text-[#16a34a]" aria-hidden="true" />
                        </div>
                        <div className="self-stretch flex flex-col items-center gap-3">
                            <h1 className={HEADING_CLASS}>
                                Check your email
                            </h1>
                            <p className={BODY_COPY_CLASS}>
                                If {submitted_email} belongs to an active account, reset instructions will arrive shortly.
                            </p>
                        </div>
                        <Link
                            to="/login"
                            className={BACK_LINK_CLASS}
                        >
                            Back to Sign In
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="self-stretch flex flex-col items-center gap-2">
                            <h1 className={HEADING_CLASS}>
                                Forgot Password?
                            </h1>
                            <p className={BODY_COPY_CLASS}>
                                Enter your email address to request password reset instructions.
                            </p>
                        </div>

                        <form
                            className="self-stretch flex flex-col items-start gap-4 text-left"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <div className="self-stretch flex flex-col items-start gap-2">
                                <label
                                    htmlFor="email_address"
                                    className={LABEL_CLASS}
                                >
                                    Email Address
                                </label>
                                <div className={email_field_class}>
                                    <input
                                        id="email_address"
                                        type="email"
                                        placeholder="your.email@company.com"
                                        className={INPUT_CLASS}
                                        {...register('email_address')}
                                    />
                                </div>
                                {errors.email_address && (
                                    <div className={FIELD_ERROR_CLASS}>
                                        {errors.email_address.message}
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={is_loading}
                                className={SUBMIT_BUTTON_CLASS}
                            >
                                {is_loading ? 'Sending...' : 'Send Reset Link'}
                            </button>
                        </form>

                        <Link
                            to="/login"
                            className={BACK_LINK_CLASS}
                        >
                            Back to Sign In
                        </Link>
                    </>
                )}
            </div>
        </AuthLayout>
    );
};
